import * as THREE from "three";
import { EnemyAct, LevelSim } from "@highmarch/domain";
import type { VFX } from "../../../../../art/vfx.js";
import { GroundMesh } from "../groundMesh";
import { roadLevelAt } from "../terrain";
import { lib, mat } from "./common";
import type { EnemyView } from "./enemyView";

/** The burrower's mound model (enemy-art pass); a procedural dome stands in until it ships. */
export const DIRT_MOUND = "dirt_mound";

/**
 * A Burrower's dive (docs/ENEMIES_V2.md), from the sim's state: it digs in (a burst of earth, its `burrow` clip, the
 * body sinking), travels as a mound of earth ploughing along the road — a trail of churned dirt behind it and the
 * struck-crosshair status on its plate, so the player sees it is there and out of reach — then breaks the surface
 * (`surface` clip, a burst, the body rising). `buried` on the enemy view (0..1) is eased here; the view sinks the body,
 * lowers the plate and fades the ring by it (enemyFeel.ts), unless the model has its own `burrow` clip to do the sinking.
 */
export const BURROW = {
  /** Target `buried` per act, and how fast it eases (per second). */
  digging: 0.5, surfacing: 0.3, ease: 3.2,
  /** Earth: dust colour, the trail's puff and decal cadence (s), the stand-in dome's size (the art's mound ships at size) and its wobble. */
  earth: [0.42, 0.3, 0.19] as [number, number, number],
  trailDark: [0.24, 0.16, 0.1] as [number, number, number],
  puffEvery: 0.07, decalEvery: 0.16, mound: 0.46, wobble: 0.08,
} as const;

/** The `buried` target for an enemy's act. Pure. */
export function buryTarget(act: EnemyAct): number {
  return act === EnemyAct.Burrowed ? 1 : act === EnemyAct.Digging ? BURROW.digging : act === EnemyAct.Surfacing ? BURROW.surfacing : 0;
}

interface Mound { obj: THREE.Object3D; base: number; puffIn: number; decalIn: number; seen: boolean; phase: number }

const _p = new THREE.Vector3();

export class BurrowFx {
  readonly group = new THREE.Group();
  private readonly mounds = new Map<number, Mound>();
  private time = 0;
  /** Set by the owner: the live view of an enemy, and its drawn feet. */
  viewOf: (id: number) => EnemyView | undefined = () => undefined;
  feet: (id: number) => THREE.Vector3 | null = () => null;

  constructor(private readonly sim: LevelSim, private readonly fx: () => VFX | null, private readonly reduced: () => boolean) {}

  /** A burst of earth where `id` digs in or breaks out (EnemyDigging / EnemySurfaced). */
  burst(id: number, out: boolean): void {
    const fx = this.reduced() ? null : this.fx(), f = this.feet(id);
    if (!fx || !f) return;
    const at = _p.copy(f).setY(ground(f.x, f.z) + 0.05);
    fx.dust(at, out ? 14 : 10, { color: BURROW.earth, size: 0.32, speed: 1.1, life: 0.7 });
    fx.debris(at, out ? 8 : 5, { color: BURROW.trailDark, size: 0.06, speed: 1.6 });
    fx.decal(at, { type: "crack", size: out ? 0.9 : 0.7, life: 2.4, hold: 1.0, color: BURROW.trailDark, bright: 0 });
    if (out) fx.ring(at, { size: 0.9, life: 0.35, color: BURROW.earth, flat: true, bright: 0.8 });
  }

  update(dt: number): void {
    this.time += dt;
    const sim = this.sim, fx = this.reduced() ? null : this.fx();
    for (const m of this.mounds.values()) m.seen = false;
    for (const e of sim.enemySlots) {
      if (!e.alive || !sim.balance.enemies[e.archetype].abilities.burrowDistance.isPositive()) continue;
      const v = this.viewOf(e.id);
      if (!v) continue;
      const target = buryTarget(e.act);
      v.buried += Math.sign(target - v.buried) * Math.min(Math.abs(target - v.buried), BURROW.ease * dt);
      if (v.buried <= 0.02) continue;
      const f = this.feet(e.id);
      if (!f) continue;
      let m = this.mounds.get(e.id);
      if (!m) { const art = artMound(); m = { obj: art ?? domeMound(), base: art ? 1 : BURROW.mound, puffIn: 0, decalIn: 0, seen: true, phase: Math.random() * 6 }; this.group.add(m.obj); this.mounds.set(e.id, m); }
      m.seen = true;
      const y = ground(f.x, f.z);
      const s = m.base * smooth(v.buried) * (1 + BURROW.wobble * Math.sin(this.time * 14 + m.phase));
      m.obj.position.set(f.x, y, f.z);
      m.obj.scale.set(s, s * (0.8 + 0.2 * Math.sin(this.time * 9 + m.phase)), s);
      m.obj.visible = true;
      if (!fx || !e.burrowed) continue;
      // the trail: earth thrown up as it ploughs on, and churned dirt left on the road behind it
      if ((m.puffIn -= dt) <= 0) { m.puffIn = BURROW.puffEvery; fx.dust(_p.set(f.x, y + 0.06, f.z), 2, { color: BURROW.earth, size: 0.22, speed: 0.45, life: 0.5 }); }
      if ((m.decalIn -= dt) <= 0) {
        m.decalIn = BURROW.decalEvery;
        fx.decal(_p.set(f.x, y + 0.02, f.z), { type: "dust", size: 0.7, life: 2.6, hold: 1.0, color: BURROW.trailDark, alpha: 0.95, bright: 0 });
        fx.debris(_p.set(f.x, y + 0.08, f.z), 2, { color: BURROW.trailDark, size: 0.05, speed: 0.9 });
      }
    }
    for (const [id, m] of this.mounds) {
      if (m.seen) continue;
      this.group.remove(m.obj);
      this.mounds.delete(id);
    }
  }

  dispose(): void { this.mounds.clear(); this.group.clear(); }
}

function ground(x: number, z: number): number { return Math.max(GroundMesh.drawnAt(x, z), roadLevelAt(x, z)); }
function smooth(t: number): number { const c = Math.max(0, Math.min(1, t)); return c * c * (3 - 2 * c); }

let domeGeo: THREE.BufferGeometry | null = null;
/** The art's `dirt_mound` (a cell-sized model, HQ_YAW-free: a mound has no front) when it ships, else null. */
function artMound(): THREE.Object3D | null {
  const L = lib();
  return L && L.hq(DIRT_MOUND) ? L.clone(`hq:${DIRT_MOUND}`) : null;
}

/** The stand-in mound: a low dome of earth with clods on it (unit radius; scaled by BURROW.mound). */
function domeMound(): THREE.Object3D {
  domeGeo ??= new THREE.SphereGeometry(1, 18, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  const g = new THREE.Group();
  const dome = new THREE.Mesh(domeGeo, mat(0x8a6238));
  dome.scale.set(1, 0.55, 1.15);
  g.add(dome);
  for (const [x, z, r] of [[0.45, 0.3, 0.28], [-0.4, -0.2, 0.22], [0.1, -0.55, 0.2]] as const) {
    const c = new THREE.Mesh(domeGeo, mat(0x4a3018));
    c.position.set(x, 0.18, z); c.scale.setScalar(r);
    g.add(c);
  }
  return g;
}
